'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useInView, useMotionValue, useSpring } from 'framer-motion';

import { ny } from '@/lib/utils';

type TNumberTickerProps = Omit<React.ComponentProps<'span'>, 'ref'> & {
    textContent: number | string;
    direction?: 'up' | 'down';
    className?: string;
    delay?: number; // delay in seconds
};

export default function NumberTicker({
    textContent = 0,
    direction = 'up',
    delay = 0,
    className,
    ...props
}: TNumberTickerProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const isInView = useInView(ref, { once: true, margin: '0px' });

    const value = typeof textContent === 'string' ? Math.pow(10, Math.max(1, textContent.length - 2)) : textContent;
    const motionValue = useMotionValue(direction === 'down' ? (value >= 100 ? value + 100 : 0) : 0);
    const springValue = useSpring(motionValue, { damping: 50, stiffness: 80, mass: 1, restDelta: 0.5 });

    useEffect(() => {
        if (isInView) {
            const timeout = setTimeout(() => {
                motionValue.set(value);
            }, delay * 1000); // Используем миллисекунды сразу

            return () => clearTimeout(timeout);
        }
    }, [motionValue, isInView, delay, direction, value]);

    const updateTextContent = useCallback(
        (latest: number) => {
            if (ref.current) {
                const rounded = Math.round(latest);
                ref.current.textContent =
                    rounded !== value ? Intl.NumberFormat('ru-RU').format(rounded) : textContent.toString();
            }
        },
        [value, textContent]
    );

    useEffect(() => {
        return springValue.on('change', updateTextContent);
    }, [springValue, updateTextContent]);

    return (
        <span
            {...props}
            className={ny('inline-block tabular-nums tracking-wider text-black dark:text-white', className)}
            ref={ref}
        >
            {textContent}
        </span>
    );
}
